/**
 * mwApp.shows module <br>
 * Work with TV shows: show/add/edit/remove
 *
 * @namespace mwApp/shows
 */

/**
 * ConfigRoutes
 * Configurates module routes
 *
 * @class ConfigRoutes
 * @memberof mwApp/shows
 * @see mwApp/shows
 *
 * @ngdoc config
 * @name mwApp/shows.config:ConfigRoutes
 */
/* @ngInject */
export default function ConfigRoutes ($stateProvider) {
  $stateProvider

    /**
     * abstract state for /shows
     */
    .state('app.shows', {
      abstract: true,
      url: '/shows',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/shows.controller:ShowsCtrl}
     * @name /shows
     * @memberof mwApp/shows.config:ConfigRoutes
     */
    .state('app.shows.list', {
      url: '',
      templateUrl: 'components/shows/views/shows.html',
      controller: 'ShowsCtrl as vm',
      label: 'TV Shows',
      access: 'viewVodSeries'
    })

    /**
     * abstract state for /:showId
     */
    .state('app.shows.show', {
      abstract: true,
      url: '/:showId',
      template: '<ui-view/>',
      resolve: {
        show: /* @ngInject */ function ($transition$, Show) {
          var { showId: id } = $transition$.params()
          return +id
            ? Show.load({ id })
            : new Show()
        },
        networks: /* @ngInject */ function ($transition$, Show) {
          var { showId: id } = $transition$.params()
          return Show.loadExtra({ id: +id || 0, url: '/networks' })
        },
        allNetworksById: /* @ngInject */ function (networks, show) {
          const networksAll = [...networks, ...show.networks]
          return networksAll.reduce((acc, item) => Object.assign(acc, { [item.id]: item }), {})
        },
        playbacks: /* @ngInject */ function ($transition$, Show) {
          var { showId: id } = $transition$.params()
          return Show.loadExtra({ id: +id || 0, url: '/playbacks' })
        },
        acls: /* @ngInject */ function ($transition$, Show) {
          var { showId: id } = $transition$.params()
          return Show.loadExtra({ id: +id || 0, url: '/acls' })
        }
      }
    })

    /**
     * controller {@link mwApp/shows.controller:AddShowCtrl}
     * @name /shows/:showId
     * @memberof mwApp/shows.config:ConfigRoutes
     */
    .state('app.shows.show.edit', {
      url: '',
      templateUrl: 'components/shows/views/addShow.html',
      controller: 'AddShowCtrl as vm',
      label: '{{::showId | addOrEdit}} TV Show',
      access: ':showId (+) ! IF createVodSeries ELSE editVodSeries'
    })

    /**
     */
    .state('app.shows.show.contentSets', {
      url: '/contentSets',
      templateUrl: 'components/common/components/assignContentSets/assignContentSets.html',
      controller: 'AssignContentSetsCtrl as vm',
      label: 'Edit Content Sets',
      resolve: {
        allNetworks: /* @ngInject */ function (networks) {
          return networks.map((network) => ({ ...network, networkId: network.networkId }))
        },
        contentItem: /* @ngInject */ function ($transition$, Show) {
          const { showId: id } = $transition$.params()
          return +id ? Show.load({ id }) : new Show()
        },
        allContentSets: /* @ngInject */ function (Show, show) {
          return Show.loadAvailableContentSets(show)
        }
      }
    })

    /**
     * abstract state for /seasonId
     */
    .state('app.shows.show.seasonContentSets', {
      url: '/seasons/:seasonId/contentSets',
      templateUrl: 'components/common/components/assignContentSets/assignContentSets.html',
      controller: 'AssignContentSetsCtrl as vm',
      label: 'Edit Content Sets',
      resolve: {
        contentItem: /* @ngInject */ function ($transition$, Season) {
          const { showId, seasonId: id } = $transition$.params()
          return Season.load({ tvShow: { id: showId }, id })
        },
        allContentSets: /* @ngInject */ function ($transition$, Season, show) {
          const { seasonId: id } = $transition$.params()
          return Season.loadAvailableContentSets(show, id)
        }
      }
    })

    /**
     * abstract state for /episodes
     */
    .state('app.shows.show.episodes', {
      abstract: true,
      url: '/seasons/:seasonId/episodes',
      template: '<ui-view/>'
    })

    /**
     * abstract state for /:episodeId
     */
    .state('app.shows.show.episodes.episode', {
      abstract: true,
      url: '/:episodeId',
      template: '<ui-view/>',
      resolve: {
        season: /* @ngInject */ function ($transition$, Season) {
          const { showId } = $transition$.params()

          const { seasonId: id } = $transition$.params()

          return +id
            ? Season.load({ tvShow: { id: showId }, id })
            : new Season({ tvShow: { id: showId } })
        },
        ep: /* @ngInject */ function ($transition$, Episode, SeasonEpisode) {
          const { showId } = $transition$.params()
          const { seasonId } = $transition$.params()
          const { episodeId: id } = $transition$.params()

          return +id
            ? (+seasonId
              ? SeasonEpisode.load({ tvShow: { id: showId }, season: { id: seasonId }, id })
              : Episode.load({ tvShow: { id: showId }, id }))
            : (+seasonId
              ? new SeasonEpisode({ tvShow: { id: showId }, season: { id: seasonId } })
              : new Episode({ tvShow: { id: showId } }))
        },
        extra: /* @ngInject */ function ($transition$, SeasonEpisode, Episode) {
          const { showId } = $transition$.params()
          const { seasonId } = $transition$.params()
          const { episodeId: id } = $transition$.params()

          return +seasonId
            ? SeasonEpisode.loadExtra({ id: +id || 0, tvShow: { id: showId }, season: { id: seasonId } })
            : Episode.loadExtra({ id: +id || 0, tvShow: { id: showId } })
        },
        playbackTokenGenerators: /* @ngInject */ function ($transition$, Show) {
          const { showId: id } = $transition$.params()
          return Show.loadExtra({ id: +id || 0, url: '/playbacks' })
        },
        networks: /* @ngInject */ function ($transition$, Show) {
          const { showId: id } = $transition$.params()
          return Show.loadExtra({ id: +id || 0, url: '/networks' })
            .then(networks =>
              [{ id: 0, name: 'All Organizations' }]
                .concat(networks.filter(n => n.id !== 0)
                  .sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))
                )
            )
        }
      }
    })

    /**
     * controller {@link mwApp/shows.controller:AddShowCtrl}
     * @name /shows/:showId/seasons/:seasonId/episodes/:episodeId
     * @memberof mwApp/shows.config:ConfigRoutes
     */
    .state('app.shows.show.episodes.episode.edit', {
      url: '',
      templateUrl: 'components/shows/views/addEpisode.html',
      controller: 'AddEpisodeCtrl as vm',
      label: '{{::episodeId | addOrEdit}} Episode',
      access: ':episodeId (+) ! IF createVodSeries ELSE editVodSeries'
    })

    /**
     */
    .state('app.shows.show.episodes.episode.contentSets', {
      url: '/contentSets',
      templateUrl: 'components/common/components/assignContentSets/assignContentSets.html',
      controller: 'AssignContentSetsCtrl as vm',
      label: 'Edit Content Sets',
      resolve: {
        allNetworks: /* @ngInject */ function (networks, show) {
          return networks.concat(show.networks)
        },
        contentItem: /* @ngInject */ function (ep) {
          return ep
        },
        allContentSets: /* @ngInject */ function ($transition$, Episode, ep) {
          const { episodeId: id } = $transition$.params()
          return Episode.loadAvailableContentSets(ep.tvShow, id)
        }
      }
    })
}
